import { BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { AnalyticsEvent } from 'utils/analytics'
import { EditionType } from '../photoStudioTypes'
import { FC } from 'react'
import { GenerateButton } from '../common/GenerateButton'
import { LAB_SDK_ENGINE } from './labSdk.types'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { externalNavigate } from 'utils/helpers/externalNavigate'
import { getPricingUrl } from 'utils/localization/pricingLinks'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

/*
 * The Lab UI for non-subscribers (FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK without an active subscription): every tool keeps
 * its options, disabled, under `LabSubscribersOnlyCard`, and `LabSubscribeButton` takes Generate's place. One call to
 * action: the card has no button of its own, the pinned one stays in view while the options scroll.
 */

interface LabSubscribersOnlyCardProps {
  /** The upsells' generic headline and body when omitted (Enhance has its own) */
  title?: string
  description?: string
  margin?: string
}

export const LabSubscribersOnlyCard: FC<LabSubscribersOnlyCardProps> = ({ title, description, margin = 0 }) => {
  const { t } = useTranslation(['photo_studio_editing', 'common'])
  return (
    <Stack gap="1.2rem" sx={{ padding: '1.6rem', margin, borderRadius: '0.8rem', backgroundColor: BEIGE_600, flexShrink: 0 }}>

      <MUIBadge size="xs" color="coral" icon={<LockOutlinedIcon fontSize="small" />} label={t('common:Subscribers_only')} sx={{ width: 'fit-content' }} />

      <Stack gap="0.6rem">

        <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>{title ?? t('non_subscribers_title')}</Typography>

        <Typography variant="text-xs" fontWeight={400} color={GRAY_700}>{description ?? t('non_subscribers_description')}</Typography>

      </Stack>

    </Stack>
  )
}

/** Generate's place for non-subscribers: the pricing page, logged like the upsells' Subscribe button */
export const LabSubscribeButton: FC<{ editionType?: EditionType }> = ({ editionType }) => {
  const { t, i18n } = useTranslation(['photo_studio_editing'])
  const { logPhotoStudioEvent, isAllFeaturesView } = useClientPhotoStudio()

  const subscribe = () => {
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAAS_UPSELL, { editionType, isAllFeaturesView, engine: LAB_SDK_ENGINE })
    externalNavigate(getPricingUrl(i18n.language))
  }

  return (
    <GenerateButton
      onGenerate={subscribe}
      label={t('subscribe_to_bkbn_button')}
      icon={<LockOutlinedIcon sx={{ fontSize: '2rem', color: 'inherit' }} />}
      helpPlacement="none"
    />
  )
}
